import { getCurrentUser } from "@/lib/auth";
import { buildTaskDocument, buildTrialDocument } from "@/services/documentBuilders";
import { getFirebaseErrorMessage, isPermissionDeniedError } from "@/lib/firebaseErrors";
import type { DocProbeResult } from "@/lib/legacyLinkResolution";
import { MAX_TOTAL_PLOTS } from "@/hooks/usePlotMapGeneration";
import { Crop } from "@/types/crop";
import { EntryCollection } from "@/types/entryCollection";
import { EntryConfigUnion } from "@/types/entryConfig";
import { CreateExperimentInput, Experiment, UpdateExperimentInput } from "@/types/experiment";
import { Location } from "@/types/location";
import { PendingInvite } from "@/types/pendingInvite";
import { Task } from "@/types/task";
import { OrganizationRole, Status, User, UserPermission } from "@/types/user";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  GeoPoint,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, ORGANIZATION_ID, requireOrganizationId } from "../lib/firebase";
import { Plot } from "../types/plot";
import { Trial } from "../types/trial";
import { parseCSVFile } from "../utils/csvUtils";
import { uploadCSVFile as storageUploadCSVFile, UploadCSVFileOptions, UploadResult } from "./storageService";

// Plot map CSV constants
export const PLOT_MAP_HEADERS = {
  PLOT_ID: "Plot ID",
  ROW: "Row",
  COLUMN: "Column",
} as const;

export const DEFAULT_PLOT_MAP_CONFIG = {
  plotIDName: PLOT_MAP_HEADERS.PLOT_ID,
  rowIDName: PLOT_MAP_HEADERS.ROW,
  colIDName: PLOT_MAP_HEADERS.COLUMN,
  headers: [PLOT_MAP_HEADERS.PLOT_ID, PLOT_MAP_HEADERS.ROW, PLOT_MAP_HEADERS.COLUMN],
};

/**
 * Dev-only diagnostic wrapper: logs how long a Firestore operation takes and
 * warns if it is still pending after 5s. A healthy emulator write completes in
 * well under a second; a multi-second/forever pend almost always means a wedged
 * connection or a stale auth session (e.g. a tab that survived an emulator
 * restart). Pair with localStorage.GLEAN_FIRESTORE_DEBUG = "1" for SDK logs.
 */
const timeFirestoreOp = async <T>(label: string, op: () => Promise<T>): Promise<T> => {
  if (!import.meta.env.DEV) return op();
  const start = Date.now();
  const pendingWarning = setTimeout(() => {
    const online = typeof navigator !== "undefined" ? navigator.onLine : "unknown";
    console.warn(
      `[firestore] ${label} still pending after 5s (navigator.onLine=${online}). ` +
        `Most likely cause in dev: MULTIPLE app tabs are open. The emulator is HTTP/1.1 and Chrome ` +
        `caps connections at 6 per host across all tabs; each tab holds hanging long-polls, so the ` +
        `write stream's requests queue for minutes. Close duplicate localhost tabs. ` +
        `For SDK logs set localStorage.GLEAN_FIRESTORE_DEBUG="1" and refresh.`
    );
  }, 5000);
  try {
    const result = await op();
    console.info(`[firestore] ${label} took ${Date.now() - start} ms`);
    return result;
  } finally {
    clearTimeout(pendingWarning);
  }
};

// Helper function to get the current user reference
const getUserReference = (): DocumentReference => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error("No authenticated user");
  }
  return doc(db, `users/${currentUser.uid}`);
};

/**
 * Pins a multi-step operation to one organization. Omit `orgId` to use the
 * current selection, resolved once at the operation's entry.
 */
export interface OrgScopedOptions {
  orgId?: string;
}

/**
 * Reads the document at `path` and reports whether it was found, is missing,
 * was refused by the security rules (denied), or could not be read (error).
 */
export const probeFirestoreDoc = async (path: string): Promise<DocProbeResult> => {
  try {
    const snap = await getDoc(doc(db, path));
    return snap.exists() ? "found" : "missing";
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return "denied";
    }
    console.warn(`Could not check whether ${path} exists:`, error);
    return "error";
  }
};

/** `probeFirestoreDoc` for a document path relative to `organizations/{orgId}/`. */
export const probeOrgDoc = (orgId: string, relativeDocPath: string): Promise<DocProbeResult> =>
  probeFirestoreDoc(`organizations/${orgId}/${relativeDocPath}`);

export const createTrial = async (trialData: Partial<Trial>): Promise<Trial | null> => {
  try {
    const trialsRef = collection(db, `organizations/${ORGANIZATION_ID}/trials`);
    const userRef = getUserReference();

    // Built, not hand-written: buildTrialDocument refuses to produce a trial
    // missing a required field. Firestore would otherwise accept the write
    // silently (ignoreUndefinedProperties) and fail later, on read, for everyone.
    const newTrial = buildTrialDocument(trialData, {
      orgId: ORGANIZATION_ID,
      timestamp: serverTimestamp() as unknown as Timestamp,
      userRef,
    });

    const docRef = await addDoc(trialsRef, newTrial);
    const trialId = docRef.id;

    // Return the created trial with ID
    return {
      id: trialId,
      ...newTrial,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error creating trial:", error);
    throw new Error(getFirebaseErrorMessage(error, "create trial"));
  }
};

export const updateTrial = async (trialId: string, trialData: Partial<Trial>): Promise<boolean> => {
  try {
    const trialRef = doc(db, `organizations/${ORGANIZATION_ID}/trials/${trialId}`);
    const userRef = getUserReference();

    // Prepare the update data object
    const updateData: Partial<Trial> = {
      ...trialData,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    // Remove id from the update data if present
    delete updateData.id;

    await updateDoc(trialRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating trial:", error);
    throw new Error(getFirebaseErrorMessage(error, "update trial"));
  }
};

export const deleteTrial = async (trialId: string): Promise<boolean> => {
  console.log(`Deleting trial ${trialId} via Cloud Function`);

  const deleteTrialFn = httpsCallable<{ orgId: string; trialId: string }, { success: boolean }>(
    functions,
    "deleteTrial"
  );

  try {
    const result = await deleteTrialFn({
      orgId: ORGANIZATION_ID,
      trialId,
    });
    console.log(`Successfully deleted trial ${trialId}`);
    return result.data.success;
  } catch (error: unknown) {
    console.error("Error deleting trial:", error);
    // Extract user-friendly message from Firebase callable error
    if (error && typeof error === "object" && "message" in error) {
      throw new Error((error as { message: string }).message);
    }
    throw new Error(getFirebaseErrorMessage(error, "delete trial"));
  }
};

// Task operations for the nested structure (trial -> experiment -> task)
export const createTask = async (
  trialId: string,
  experimentId: string,
  taskData: Partial<Task>
): Promise<Task | null> => {
  try {
    const tasksRef = collection(
      db,
      `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/tasks`
    );
    const userRef = getUserReference();

    // Built, not hand-written. The previous literal copied four required fields
    // straight from input with no defaults, and the `Omit<Task, "id">` annotation
    // that looked like protection was erased by an `as any` at the call site.
    const newTask = buildTaskDocument(taskData, {
      orgId: ORGANIZATION_ID,
      timestamp: serverTimestamp() as unknown as Timestamp,
      userRef,
    });

    const docRef = await addDoc(tasksRef, newTask);

    // Return the created task with ID
    return {
      id: docRef.id,
      ...newTask,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error creating task:", error);
    throw new Error(getFirebaseErrorMessage(error, "create task"));
  }
};

export const updateTask = async (
  trialId: string,
  experimentId: string,
  taskId: string,
  updates: Partial<Task>
): Promise<boolean> => {
  try {
    const taskRef = doc(
      db,
      `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}`
    );
    const userRef = getUserReference();

    // Prepare the update data object
    const updateData: Partial<Task> = {
      ...updates,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    // Remove id from the update data if present
    delete updateData.id;

    await updateDoc(taskRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating task:", error);
    throw new Error(getFirebaseErrorMessage(error, "update task"));
  }
};

export const deleteTask = async (trialId: string, experimentId: string, taskId: string): Promise<boolean> => {
  console.log(`Deleting task ${taskId} via Cloud Function`);

  const deleteTaskFn = httpsCallable<
    { orgId: string; trialId: string; experimentId: string; taskId: string },
    { success: boolean }
  >(functions, "deleteTask");

  try {
    const result = await deleteTaskFn({
      orgId: ORGANIZATION_ID,
      trialId,
      experimentId,
      taskId,
    });
    console.log(`Successfully deleted task ${taskId}`);
    return result.data.success;
  } catch (error: unknown) {
    console.error("Error deleting task:", error);
    // Extract user-friendly message from Firebase callable error
    if (error && typeof error === "object" && "message" in error) {
      throw new Error((error as { message: string }).message);
    }
    throw new Error(getFirebaseErrorMessage(error, "delete task"));
  }
};

// Organization Member Management Functions

/**
 * Create a pending invite for a user to join the organization.
 * The user document and permissions will be created by the Cloud Function when they authenticate.
 */
export const createPendingInvite = async (
  email: string,
  firstName: string,
  lastName: string,
  role: OrganizationRole
): Promise<{ success: boolean; error?: string }> => {
  try {
    const emailLower = email.toLowerCase();
    const inviteRef = doc(db, `organizations/${ORGANIZATION_ID}/pendingInvites/${emailLower}`);

    // Check if invite already exists
    const existingInvite = await getDoc(inviteRef);
    if (existingInvite.exists()) {
      return {
        success: false,
        error: "An invite already exists for this email address",
      };
    }

    // Note: We intentionally do NOT check if the user already has a membership here.
    // Querying the users collection by email would require listing all users, which
    // is blocked by security rules (prevents user enumeration attacks).
    // The Cloud Function that processes invite claims should check for existing
    // membership using Admin SDK and handle duplicates appropriately.

    // Create the pending invite - email stored lowercase for case-insensitive matching
    const inviteData: Omit<PendingInvite, "createdAt"> = {
      email: emailLower,
      firstName,
      lastName,
      role,
      createdBy: getUserReference(),
    };

    await setDoc(inviteRef, {
      ...inviteData,
      createdAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error creating pending invite:", error);
    return {
      success: false,
      error: getFirebaseErrorMessage(error, "create invite"),
    };
  }
};

/**
 * Cancel/delete a pending invite by its document ID (lowercased email)
 */
export const cancelPendingInvite = async (inviteId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const inviteRef = doc(db, `organizations/${ORGANIZATION_ID}/pendingInvites/${inviteId}`);
    await deleteDoc(inviteRef);
    return { success: true };
  } catch (error) {
    console.error("Error canceling pending invite:", error);
    return {
      success: false,
      error: getFirebaseErrorMessage(error, "cancel invite"),
    };
  }
};

// Response type for permission management operations
interface PermissionOperationResult {
  success: boolean;
  error?: {
    code: string;
    message: string;
  };
}

export const updateOrganizationUser = async (
  userId: string,
  updates: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: OrganizationRole;
    status?: Status;
  }
): Promise<PermissionOperationResult> => {
  console.log(`Updating user ${userId} via Cloud Function`);

  const updateUserFn = httpsCallable<
    {
      orgId: string;
      targetUserId: string;
      updates: {
        firstName?: string;
        lastName?: string;
        role?: OrganizationRole;
        status?: Status;
      };
    },
    PermissionOperationResult
  >(functions, "updateOrganizationUser");

  try {
    // Build updates object (email is not editable, so we exclude it)
    const updatePayload: {
      firstName?: string;
      lastName?: string;
      role?: OrganizationRole;
      status?: Status;
    } = {};

    if (updates.firstName !== undefined) {
      updatePayload.firstName = updates.firstName;
    }
    if (updates.lastName !== undefined) {
      updatePayload.lastName = updates.lastName;
    }
    if (updates.role !== undefined) {
      updatePayload.role = updates.role;
    }
    if (updates.status !== undefined) {
      updatePayload.status = updates.status;
    }

    const result = await updateUserFn({
      orgId: ORGANIZATION_ID,
      targetUserId: userId,
      updates: updatePayload,
    });

    return result.data;
  } catch (error: unknown) {
    console.error("Error updating organization member:", error);
    // Extract user-friendly message from Firebase callable error
    if (error && typeof error === "object" && "message" in error) {
      return {
        success: false,
        error: {
          code: "UNKNOWN",
          message: (error as { message: string }).message,
        },
      };
    }
    return {
      success: false,
      error: {
        code: "UNKNOWN",
        message: getFirebaseErrorMessage(error, "update team member"),
      },
    };
  }
};

export const removeOrganizationUser = async (
  userId: string,
  deleteUserDocument: boolean = false
): Promise<PermissionOperationResult> => {
  console.log(`Removing user ${userId} via Cloud Function`);

  // Note: deleteUserDocument is ignored - user documents are preserved for data integrity
  // The Cloud Function only removes the permission, not the user document

  const removeUserFn = httpsCallable<{ orgId: string; targetUserId: string }, PermissionOperationResult>(
    functions,
    "removeOrganizationUser"
  );

  try {
    const result = await removeUserFn({
      orgId: ORGANIZATION_ID,
      targetUserId: userId,
    });

    return result.data;
  } catch (error: unknown) {
    console.error("Error removing organization member:", error);
    // Extract user-friendly message from Firebase callable error
    if (error && typeof error === "object" && "message" in error) {
      return {
        success: false,
        error: {
          code: "UNKNOWN",
          message: (error as { message: string }).message,
        },
      };
    }
    return {
      success: false,
      error: {
        code: "UNKNOWN",
        message: getFirebaseErrorMessage(error, "remove team member"),
      },
    };
  }
};

/**
 * Invite a user to join the organization.
 * Creates a pending invite that will be processed when the user authenticates.
 */
export const inviteOrganizationUser = async (
  email: string,
  firstName: string,
  lastName: string,
  role: OrganizationRole
): Promise<{ success: boolean; error?: string }> => {
  return await createPendingInvite(email, firstName, lastName, role);
};

export const activateOrganizationUser = async (userId: string): Promise<PermissionOperationResult> => {
  return await updateOrganizationUser(userId, { status: "active" });
};

export const disableOrganizationUser = async (userId: string): Promise<PermissionOperationResult> => {
  return await updateOrganizationUser(userId, { status: "disabled" });
};

export const changeOrganizationUserRole = async (
  userId: string,
  newRole: OrganizationRole
): Promise<PermissionOperationResult> => {
  return await updateOrganizationUser(userId, { role: newRole });
};

// Experiment operations
export const createExperiment = async (
  trialId: string,
  experimentData: CreateExperimentInput
): Promise<Experiment | null> => {
  try {
    const experimentsRef = collection(db, `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments`);
    const userRef = getUserReference();

    const newExperiment: Omit<Experiment, "id"> = {
      orgId: ORGANIZATION_ID,
      name: experimentData.name || "Default Experiment",
      description: experimentData.description || "Default experiment for trial",
      location: experimentData.location || "",
      ...(experimentData.plotMap && { plotMap: experimentData.plotMap }),
      metadata: experimentData.metadata || {},
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
    };

    const docRef = await addDoc(experimentsRef, newExperiment);

    return {
      id: docRef.id,
      ...newExperiment,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error creating experiment:", error);
    throw new Error(getFirebaseErrorMessage(error, "create experiment"));
  }
};

export const updateExperiment = async (
  trialId: string,
  experimentId: string,
  experimentData: UpdateExperimentInput
): Promise<boolean> => {
  try {
    const experimentRef = doc(db, `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}`);
    const userRef = getUserReference();

    const updateData: UpdateExperimentInput = {
      ...experimentData,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    delete updateData.id;

    await updateDoc(experimentRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating experiment:", error);
    throw new Error(getFirebaseErrorMessage(error, "update experiment"));
  }
};

export const deleteExperiment = async (
  trialId: string,
  experimentId: string,
  options?: OrgScopedOptions
): Promise<boolean> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    const experimentRef = doc(db, `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}`);
    await deleteDoc(experimentRef);
    return true;
  } catch (error) {
    console.error("Error deleting experiment:", error);
    throw new Error(getFirebaseErrorMessage(error, "delete experiment"));
  }
};

/**
 * Check if an experiment has any tasks.
 * Used to determine if plot map modifications should be locked.
 * Once a task exists, users may have cached plot data on their mobile devices,
 * so we prevent plot changes to avoid orphaned entry collections.
 */
export const checkExperimentHasTasks = async (
  trialId: string,
  experimentId: string,
  options?: OrgScopedOptions
): Promise<boolean> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    const tasksRef = collection(
      db,
      `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}/tasks`
    );
    const tasksSnapshot = await getDocs(tasksRef);
    return tasksSnapshot.size > 0;
  } catch (error) {
    console.error("Error checking experiment tasks:", error);
    // Fail-safe: assume tasks exist to prevent accidental data issues
    return true;
  }
};

/**
 * Creates an experiment with optional plot map from CSV file or grid generation.
 * This combines experiment creation with plot creation in a single operation.
 */
export const createExperimentWithPlots = async (
  trialId: string,
  experimentData: CreateExperimentInput,
  csvFile?: File,
  onProgress?: PlotCreationProgressCallback,
  options?: OrgScopedOptions
): Promise<Experiment | null> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    const experimentsRef = collection(db, `organizations/${orgId}/trials/${trialId}/experiments`);
    const userRef = getUserReference();

    const newExperiment: Omit<Experiment, "id"> = {
      orgId,
      name: experimentData.name || "New Experiment",
      description: experimentData.description || "",
      location: experimentData.location || "",
      ...(experimentData.plotMap && { plotMap: experimentData.plotMap }),
      metadata: experimentData.metadata || {},
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
    };

    const docRef = await addDoc(experimentsRef, newExperiment);
    const experimentId = docRef.id;

    // Handle CSV file upload and plot creation if provided
    if (csvFile && experimentData.plotMap) {
      try {
        const plotCreationResult = await createPlotsFromCSV(
          trialId,
          experimentId,
          csvFile,
          {
            plotIDName: experimentData.plotMap.plotIDName,
            rowIDName: experimentData.plotMap.rowIDName,
            colIDName: experimentData.plotMap.colIDName,
            headers: experimentData.plotMap.headers,
          },
          onProgress,
          { orgId }
        );

        if (plotCreationResult.success) {
          console.log(`Successfully created ${plotCreationResult.createdCount} plots from CSV`);
          if (plotCreationResult.errors && plotCreationResult.errors.length > 0) {
            console.warn("Plot creation warnings:", plotCreationResult.errors);
          }
        } else {
          // Rollback: delete the experiment since plot creation failed
          console.error("Failed to create plots from CSV:", plotCreationResult.errors);
          onProgress?.(0, "Plot creation failed. Rolling back experiment...");
          try {
            await deleteExperiment(trialId, experimentId, { orgId });
          } catch (rollbackError) {
            console.error("Failed to rollback experiment after plot creation failure:", rollbackError);
          }
          throw new Error(`Plot creation failed: ${plotCreationResult.errors?.join(", ") || "Unknown error"}`);
        }
      } catch (uploadError) {
        // Rollback: delete the experiment on any unexpected error
        console.error("Error creating plots from CSV:", uploadError);
        onProgress?.(0, "Error occurred. Rolling back experiment...");
        try {
          await deleteExperiment(trialId, experimentId, { orgId });
        } catch (rollbackError) {
          console.error("Failed to rollback experiment after error:", rollbackError);
        }
        throw uploadError;
      }
    }

    return {
      id: experimentId,
      ...newExperiment,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error creating experiment with plots:", error);
    throw new Error(getFirebaseErrorMessage(error, "create experiment"));
  }
};

/**
 * Duplicate an experiment and all its plots.
 * Creates a new experiment with copied fields, then batch-copies all plots.
 * Rolls back on failure (deletes experiment + any partially created plots).
 */
export const duplicateExperimentWithPlots = async (
  trialId: string,
  sourceExperiment: Experiment,
  sourcePlots: Plot[],
  overrides: { name: string; description: string; location: string },
  onProgress?: PlotCreationProgressCallback,
  options?: OrgScopedOptions
): Promise<Experiment | null> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    const experimentsRef = collection(db, `organizations/${orgId}/trials/${trialId}/experiments`);
    const userRef = getUserReference();

    onProgress?.(0.05, "Creating experiment...");

    // Create the new experiment document (overrides are always provided by the dialog)
    const newExperiment: Omit<Experiment, "id"> = {
      orgId,
      name: overrides.name,
      description: overrides.description,
      location: overrides.location,
      ...(sourceExperiment.plotMap && { plotMap: sourceExperiment.plotMap }),
      metadata: sourceExperiment.metadata || {},
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
    };

    const docRef = await addDoc(experimentsRef, newExperiment);
    const newExperimentId = docRef.id;

    // Batch-copy plots if there are any
    if (sourcePlots.length > 0) {
      onProgress?.(0.1, `Copying ${sourcePlots.length.toLocaleString()} plots...`);

      const plotsCollectionPath = `organizations/${orgId}/trials/${trialId}/experiments/${newExperimentId}/plots`;

      // Prepare batches
      const batches: BatchWithMetadata[] = [];
      for (let i = 0; i < sourcePlots.length; i += BATCH_SIZE) {
        const batchPlots = sourcePlots.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);

        for (const plot of batchPlots) {
          const plotRef = doc(db, plotsCollectionPath, plot.plotID);
          const plotData = {
            plotID: plot.plotID,
            ...(plot.row != null && { row: plot.row }),
            ...(plot.column != null && { column: plot.column }),
            ...(plot.plotMapUploadPosition != null && { plotMapUploadPosition: plot.plotMapUploadPosition }),
            ...(plot.location && { location: plot.location }),
            ...(plot.metadata && { metadata: plot.metadata }),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: userRef,
            updatedBy: userRef,
          };
          batch.set(plotRef, plotData);
        }

        batches.push({
          batch,
          plotCount: batchPlots.length,
          batchIndex: batches.length,
        });
      }

      // Execute batches with progress scaled from 0.1 to 1.0
      const result = await executeBatchesInParallel(
        batches,
        PARALLEL_BATCHES,
        sourcePlots.length,
        (progress, message) => {
          onProgress?.(0.1 + progress * 0.9, message);
        }
      );

      if (!result.success) {
        console.error("[DuplicateExperiment] Plot duplication failed, initiating rollback...");
        onProgress?.(0, "Plot duplication failed. Rolling back...");
        await rollbackPlots(orgId, trialId, newExperimentId, onProgress);
        try {
          await deleteExperiment(trialId, newExperimentId, { orgId });
        } catch (rollbackError) {
          console.error("Failed to rollback experiment after plot duplication failure:", rollbackError);
        }
        throw new Error(`Plot duplication failed: ${result.errors.join(", ")}`);
      }
    }

    return {
      id: newExperimentId,
      ...newExperiment,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error duplicating experiment:", error);
    throw new Error(getFirebaseErrorMessage(error, "duplicate experiment"));
  }
};

// Plot operations
export const createPlot = async (
  trialId: string,
  experimentId: string,
  plotData: Partial<Plot>
): Promise<Plot | null> => {
  try {
    const userRef = getUserReference();

    // If plotID is provided, use it as document ID; otherwise generate one
    const documentId = plotData.plotID || `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const plotRef = doc(
      db,
      `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/plots/${documentId}`
    );

    const newPlot: Omit<Plot, "plotID"> = {
      row: plotData.row || 1,
      column: plotData.column || 1,
      plotMapUploadPosition: plotData.plotMapUploadPosition || 1,
      location: plotData.location || new GeoPoint(0, 0),
      metadata: plotData.metadata || {},
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
    };

    await setDoc(plotRef, newPlot);

    return {
      plotID: documentId,
      ...newPlot,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error creating plot:", error);
    throw new Error(getFirebaseErrorMessage(error, "create plot"));
  }
};

export const updatePlot = async (
  trialId: string,
  experimentId: string,
  plotId: string,
  plotData: Partial<Plot>,
  options?: OrgScopedOptions
): Promise<boolean> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  // Prevent plot modifications when tasks exist (users may have cached plot data)
  const hasTasks = await checkExperimentHasTasks(trialId, experimentId, { orgId });
  if (hasTasks) {
    throw new Error("Cannot modify plots: tasks exist for this experiment. Delete all tasks first.");
  }

  try {
    const plotRef = doc(
      db,
      `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}/plots/${plotId}`
    );
    const userRef = getUserReference();

    const updateData: Partial<Plot> = {
      ...plotData,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    delete updateData.plotID;

    await updateDoc(plotRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating plot:", error);
    throw new Error(getFirebaseErrorMessage(error, "update plot"));
  }
};

export const deletePlot = async (
  trialId: string,
  experimentId: string,
  plotId: string,
  options?: OrgScopedOptions
): Promise<boolean> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  // Prevent plot deletion when tasks exist (users may have cached plot data)
  const hasTasks = await checkExperimentHasTasks(trialId, experimentId, { orgId });
  if (hasTasks) {
    throw new Error("Cannot delete plots: tasks exist for this experiment. Delete all tasks first.");
  }

  try {
    const plotRef = doc(
      db,
      `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}/plots/${plotId}`
    );
    await deleteDoc(plotRef);
    return true;
  } catch (error) {
    console.error("Error deleting plot:", error);
    throw new Error(getFirebaseErrorMessage(error, "delete plot"));
  }
};

// Progress callback type for plot creation
export type PlotCreationProgressCallback = (progress: number, message: string) => void;

// Constants for parallel batch creation
const BATCH_SIZE = 500; // Firestore batch limit
const PARALLEL_BATCHES = 10; // Number of batches to commit in parallel (increased for speed)
const MAX_RETRIES = 3; // Maximum retry attempts per batch
const BASE_RETRY_DELAY_MS = 1000; // Base delay for exponential backoff (1s, 2s, 4s)

// Type for batch with metadata
interface BatchWithMetadata {
  batch: ReturnType<typeof writeBatch>;
  plotCount: number;
  batchIndex: number;
}

// Type for batch commit result
interface BatchCommitResult {
  success: boolean;
  plotCount: number;
  batchIndex: number;
  error?: string;
}

/**
 * Commit a batch with exponential backoff retry
 */
const commitBatchWithRetry = async (
  batchWithMeta: BatchWithMetadata,
  maxRetries: number = MAX_RETRIES
): Promise<BatchCommitResult> => {
  const { batch, plotCount, batchIndex } = batchWithMeta;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await batch.commit();
      return { success: true, plotCount, batchIndex };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";

      if (attempt < maxRetries) {
        const delay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
        console.warn(
          `Batch ${batchIndex} failed (attempt ${attempt}/${maxRetries}), retrying in ${delay}ms: ${errorMessage}`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        console.error(`Batch ${batchIndex} failed after ${maxRetries} attempts: ${errorMessage}`);
        return {
          success: false,
          plotCount,
          batchIndex,
          error: `Batch ${batchIndex} failed after ${maxRetries} attempts: ${errorMessage}`,
        };
      }
    }
  }

  return { success: false, plotCount, batchIndex, error: "Unknown error" };
};

/**
 * Execute batches in parallel chunks with progress reporting
 */
const executeBatchesInParallel = async (
  batches: BatchWithMetadata[],
  parallelCount: number,
  totalPlots: number,
  onProgress?: PlotCreationProgressCallback
): Promise<{ success: boolean; createdCount: number; errors: string[] }> => {
  const errors: string[] = [];
  let createdCount = 0;

  for (let i = 0; i < batches.length; i += parallelCount) {
    const chunk = batches.slice(i, i + parallelCount);

    // Execute chunk in parallel
    const results = await Promise.all(chunk.map((batch) => commitBatchWithRetry(batch)));

    // Process results
    for (const result of results) {
      if (result.success) {
        createdCount += result.plotCount;
      } else if (result.error) {
        errors.push(result.error);
      }
    }

    // If any batch failed, stop processing and return
    if (errors.length > 0) {
      return { success: false, createdCount, errors };
    }

    // Update progress after each chunk for snappy UI feedback
    const progress = Math.min(createdCount / totalPlots, 1);
    onProgress?.(progress, `Created ${createdCount.toLocaleString()} of ${totalPlots.toLocaleString()} plots...`);

    // Log progress for large operations
    if (totalPlots > 1000) {
      console.log(
        `Progress: ${createdCount.toLocaleString()}/${totalPlots.toLocaleString()} plots (${Math.round(progress * 100)}%)`
      );
    }
  }

  return { success: true, createdCount, errors: [] };
};

/**
 * Rollback created plots by deleting all plots for an experiment
 * Used when plot creation fails partway through
 */
const rollbackPlots = async (
  orgId: string,
  trialId: string,
  experimentId: string,
  onProgress?: PlotCreationProgressCallback
): Promise<void> => {
  onProgress?.(0, "Rolling back created plots...");

  const plotsRef = collection(
    db,
    `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}/plots`
  );

  const plotsSnapshot = await getDocs(plotsRef);

  if (plotsSnapshot.empty) {
    onProgress?.(1, "No plots to rollback");
    return;
  }

  const plotRefs = plotsSnapshot.docs.map((docSnap) => docSnap.ref);
  const totalPlots = plotRefs.length;
  let deletedCount = 0;

  // Delete in batches of 500
  for (let i = 0; i < plotRefs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const batchRefs = plotRefs.slice(i, i + BATCH_SIZE);

    for (const ref of batchRefs) {
      batch.delete(ref);
    }

    await batch.commit();
    deletedCount += batchRefs.length;

    const progress = deletedCount / totalPlots;
    onProgress?.(
      progress,
      `Rolling back... deleted ${deletedCount.toLocaleString()} of ${totalPlots.toLocaleString()} plots`
    );
  }

  console.log(`Rollback complete: deleted ${deletedCount} plots`);
  onProgress?.(1, "Rollback complete");
};

// Core plot creation from parsed data with parallel batch processing
const createPlotsFromParsedData = async (
  orgId: string,
  trialId: string,
  experimentId: string,
  parsedData: Record<string, string>[],
  plotMapConfig: {
    plotIDName: string;
    rowIDName?: string;
    colIDName?: string;
    headers: string[];
  },
  onProgress?: PlotCreationProgressCallback
): Promise<{ success: boolean; createdCount: number; errors?: string[] }> => {
  const startTime = performance.now();
  console.log(`[PlotCreation] Starting for ${parsedData?.length || 0} plots`);

  try {
    if (!parsedData || parsedData.length === 0) {
      return { success: false, createdCount: 0, errors: ["Parsed data is empty"] };
    }

    // Validate that the required plot ID column exists
    if (!plotMapConfig.plotIDName || !(plotMapConfig.plotIDName in parsedData[0])) {
      return {
        success: false,
        createdCount: 0,
        errors: [`Plot ID column '${plotMapConfig.plotIDName}' not found in parsed data`],
      };
    }

    const userRef = getUserReference();
    const validationErrors: string[] = [];
    const totalPlots = parsedData.length;

    console.log(`[PlotCreation] Phase 1: Preparing plot data for ${totalPlots} plots...`);
    const prepStartTime = performance.now();

    // Prepare all plot data first (to separate validation from writing)
    const plotsToCreate: Array<{
      plotRef: DocumentReference;
      plotData: Omit<Plot, "plotID">;
      rowIndex: number;
    }> = [];

    onProgress?.(0, `Preparing ${totalPlots.toLocaleString()} plots...`);

    // Process each row in the parsed data to prepare plot data
    for (let i = 0; i < parsedData.length; i++) {
      const row = parsedData[i];

      try {
        // Extract plot ID from the designated column
        const plotID = row[plotMapConfig.plotIDName]?.trim();

        if (!plotID) {
          validationErrors.push(`Row ${i + 1}: Missing plot ID in column '${plotMapConfig.plotIDName}'`);
          continue;
        }

        // Validate Firestore document ID constraints
        if (plotID.includes("/") || plotID === "." || plotID === ".." || plotID.length > 1500) {
          const reason = plotID.includes("/")
            ? 'contains "/"'
            : plotID === "." || plotID === ".."
              ? "is a reserved name"
              : "exceeds 1500 characters";
          validationErrors.push(`Row ${i + 1}: Invalid plot ID "${plotID}" (${reason})`);
          continue;
        }

        // Extract row and column if specified
        const plotRow =
          plotMapConfig.rowIDName && row[plotMapConfig.rowIDName]
            ? parseInt(row[plotMapConfig.rowIDName].trim(), 10)
            : undefined;
        const plotCol =
          plotMapConfig.colIDName && row[plotMapConfig.colIDName]
            ? parseInt(row[plotMapConfig.colIDName].trim(), 10)
            : undefined;

        // Create metadata from all other columns (excluding the ID columns)
        const metadata: Record<string, string> = {};
        for (const [key, value] of Object.entries(row)) {
          // Skip the columns used for plot identification and positioning
          if (
            key !== plotMapConfig.plotIDName &&
            key !== plotMapConfig.rowIDName &&
            key !== plotMapConfig.colIDName &&
            value?.trim()
          ) {
            metadata[key] = value.trim();
          }
        }

        // Create plot document reference with user-provided plot ID
        const plotRef = doc(
          db,
          `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}/plots/${plotID}`
        );

        // Create plot data
        const plotData: Omit<Plot, "plotID"> = {
          row: plotRow,
          column: plotCol,
          plotMapUploadPosition: i + 1, // Position in the data
          location: new GeoPoint(0, 0), // Default location
          metadata,
          createdAt: serverTimestamp() as unknown as Timestamp,
          updatedAt: serverTimestamp() as unknown as Timestamp,
          createdBy: userRef,
          updatedBy: userRef,
        };

        plotsToCreate.push({ plotRef, plotData, rowIndex: i + 1 });
      } catch (error) {
        validationErrors.push(
          `Row ${i + 1}: ${error instanceof Error ? error.message : "Unknown error processing row"}`
        );
      }
    }

    // If there are validation errors for all rows, fail early
    if (plotsToCreate.length === 0) {
      return {
        success: false,
        createdCount: 0,
        errors: validationErrors.length > 0 ? validationErrors : ["No valid plots to create"],
      };
    }

    const prepEndTime = performance.now();
    console.log(
      `[PlotCreation] Phase 1 complete: ${plotsToCreate.length} plots prepared in ${(prepEndTime - prepStartTime).toFixed(0)}ms`
    );

    console.log(`[PlotCreation] Phase 2: Creating ${Math.ceil(plotsToCreate.length / BATCH_SIZE)} batches...`);
    const batchStartTime = performance.now();

    // Prepare batches for parallel execution
    const batches: BatchWithMetadata[] = [];
    for (let i = 0; i < plotsToCreate.length; i += BATCH_SIZE) {
      const batchPlots = plotsToCreate.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      for (const { plotRef, plotData } of batchPlots) {
        batch.set(plotRef, plotData);
      }

      batches.push({
        batch,
        plotCount: batchPlots.length,
        batchIndex: batches.length,
      });
    }

    const batchEndTime = performance.now();
    console.log(
      `[PlotCreation] Phase 2 complete: ${batches.length} batches created in ${(batchEndTime - batchStartTime).toFixed(0)}ms`
    );

    console.log(`[PlotCreation] Phase 3: Executing ${batches.length} batches (${PARALLEL_BATCHES} parallel)...`);
    const execStartTime = performance.now();

    // Execute batches in parallel
    const result = await executeBatchesInParallel(batches, PARALLEL_BATCHES, plotsToCreate.length, onProgress);

    const execEndTime = performance.now();
    console.log(
      `[PlotCreation] Phase 3 complete: ${result.createdCount} plots written in ${(execEndTime - execStartTime).toFixed(0)}ms`
    );

    // If creation failed, rollback any created plots
    if (!result.success) {
      console.error("[PlotCreation] Plot creation failed, initiating rollback...");
      await rollbackPlots(orgId, trialId, experimentId, onProgress);

      return {
        success: false,
        createdCount: 0,
        errors: result.errors,
      };
    }

    const totalTime = performance.now() - startTime;
    console.log(`[PlotCreation] SUCCESS: Created ${result.createdCount} plots in ${(totalTime / 1000).toFixed(2)}s`);
    console.log(
      `[PlotCreation] Breakdown: Prep=${(prepEndTime - prepStartTime).toFixed(0)}ms, Batch=${(batchEndTime - batchStartTime).toFixed(0)}ms, Exec=${(execEndTime - execStartTime).toFixed(0)}ms`
    );

    onProgress?.(1, `Completed! Created ${result.createdCount.toLocaleString()} plots.`);

    return {
      success: true,
      createdCount: result.createdCount,
      errors: validationErrors.length > 0 ? validationErrors : undefined,
    };
  } catch (error) {
    console.error("Error creating plots from parsed data:", error);

    // Attempt rollback on unexpected errors
    try {
      await rollbackPlots(orgId, trialId, experimentId, onProgress);
    } catch (rollbackError) {
      console.error("Rollback failed:", rollbackError);
    }

    return {
      success: false,
      createdCount: 0,
      errors: [getFirebaseErrorMessage(error, "create plots")],
    };
  }
};

/**
 * Pre-validates plot IDs from CSV without creating any plots.
 * Returns list of validation errors for invalid plot IDs.
 */
export const validatePlotIDsFromCSV = async (
  csvFile: File,
  plotMapConfig: {
    plotIDName: string;
    headers: string[];
  }
): Promise<{
  isValid: boolean;
  errors: Array<{ row: number; plotID: string; reason: string }>;
  validCount: number;
  totalCount: number;
}> => {
  try {
    const csvData = await parseCSVFile(csvFile);

    if (!csvData || csvData.length === 0) {
      return { isValid: false, errors: [], validCount: 0, totalCount: 0 };
    }

    const validationErrors: Array<{ row: number; plotID: string; reason: string }> = [];
    let validCount = 0;

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      const plotID = row[plotMapConfig.plotIDName]?.trim();

      if (!plotID) {
        validationErrors.push({
          row: i + 1,
          plotID: row[plotMapConfig.plotIDName] || "(empty)",
          reason: "Missing plot ID",
        });
        continue;
      }

      // Check for invalid Firestore document ID characters
      if (plotID.includes("/")) {
        validationErrors.push({
          row: i + 1,
          plotID,
          reason: 'Contains "/" (path separator)',
        });
        continue;
      }

      if (plotID === "." || plotID === "..") {
        validationErrors.push({
          row: i + 1,
          plotID,
          reason: "Reserved name",
        });
        continue;
      }

      if (plotID.length > 1500) {
        validationErrors.push({
          row: i + 1,
          plotID: plotID.substring(0, 50) + "...",
          reason: "Exceeds 1500 characters",
        });
        continue;
      }

      validCount++;
    }

    return {
      isValid: validationErrors.length === 0,
      errors: validationErrors,
      validCount,
      totalCount: csvData.length,
    };
  } catch (error) {
    console.error("Error validating plot IDs from CSV:", error);
    throw error;
  }
};

// Batch plot creation from CSV data
export const createPlotsFromCSV = async (
  trialId: string,
  experimentId: string,
  csvFile: File,
  plotMapConfig: {
    plotIDName: string;
    rowIDName?: string;
    colIDName?: string;
    headers: string[];
  },
  onProgress?: PlotCreationProgressCallback,
  options?: OrgScopedOptions
): Promise<{ success: boolean; createdCount: number; errors?: string[] }> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    // Parse the CSV file
    const csvData = await parseCSVFile(csvFile);

    // Use the core function with parsed data
    return await createPlotsFromParsedData(orgId, trialId, experimentId, csvData, plotMapConfig, onProgress);
  } catch (error) {
    console.error("Error creating plots from CSV:", error);
    return {
      success: false,
      createdCount: 0,
      errors: [getFirebaseErrorMessage(error, "create plots from CSV")],
    };
  }
};

// Plot Map Generation Utilities

/**
 * Generates a plot ID following ARM naming convention with collision prevention
 * @param row - Row number
 * @param col - Column number
 * @param totalRows - Total number of rows in the grid (used to determine padding)
 * @param totalCols - Total number of columns in the grid (used to determine padding)
 * @returns ARM-formatted plot ID string with consistent padding to prevent collisions
 */
const getARMPlotID = (row: number, col: number, totalRows: number, totalCols: number): string => {
  // Determine minimum digits needed for row padding to prevent collisions
  const rowPadding = String(totalRows).length;

  // Use ARM convention for column padding
  let colString: string;
  if (totalCols < 10) {
    colString = col.toString().padStart(2, "0"); // Always 2 digits minimum
  } else if (totalCols < 100) {
    colString = col.toString().padStart(2, "0"); // 2 digits for up to 99 columns
  } else {
    colString = col < 10 ? col.toString().padStart(2, "0") : col.toString(); // ARM's actual rule
  }

  // Pad row to prevent collisions
  const rowString = row.toString().padStart(rowPadding, "0");

  return rowString + colString;
};

/**
 * Generates a CSV string for a plot map with the specified dimensions
 * Uses ARM format for plot IDs following ARM GDM naming convention:
 * - Row: used as-is (no padding)
 * - Column: zero-padded to 2 digits if < 10, otherwise used in full
 * Examples: Row 5, Col 2 → "502"; Row 12, Col 101 → "12101"
 * @param rows - Number of rows in the plot map
 * @param cols - Number of columns in the plot map
 * @returns CSV content string with Plot ID, Row, and Column headers
 */
export const generatePlotMapCSV = (rows: number, cols: number): string => {
  let csvContent = `${PLOT_MAP_HEADERS.PLOT_ID},${PLOT_MAP_HEADERS.ROW},${PLOT_MAP_HEADERS.COLUMN}\n`;

  for (let row = 1; row <= rows; row++) {
    for (let col = 1; col <= cols; col++) {
      const plotId = getARMPlotID(row, col, rows, cols);
      csvContent += `${plotId},${row},${col}\n`;
    }
  }

  return csvContent;
};

/**
 * Creates plots from a grid specification (rows x columns) without requiring a CSV file
 * Generates parsed data directly and uses the core plot creation logic
 * @param trialId - The trial ID to create plots for
 * @param experimentId - The experiment ID to create plots for
 * @param rows - Number of rows in the plot map
 * @param cols - Number of columns in the plot map
 * @returns Promise with creation results
 */
export const createPlotsFromGrid = async (
  trialId: string,
  experimentId: string,
  rows: number,
  cols: number,
  onProgress?: PlotCreationProgressCallback,
  options?: OrgScopedOptions
): Promise<{ success: boolean; createdCount: number; errors?: string[] }> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    // Validate input parameters
    if (rows <= 0 || cols <= 0) {
      return {
        success: false,
        createdCount: 0,
        errors: ["Rows and columns must be positive numbers"],
      };
    }

    // Generate parsed data directly (same format as parseCSVFile returns)
    const parsedData: Record<string, string>[] = [];

    for (let row = 1; row <= rows; row++) {
      for (let col = 1; col <= cols; col++) {
        const plotId = getARMPlotID(row, col, rows, cols);
        parsedData.push({
          [PLOT_MAP_HEADERS.PLOT_ID]: plotId,
          [PLOT_MAP_HEADERS.ROW]: row.toString(),
          [PLOT_MAP_HEADERS.COLUMN]: col.toString(),
        });
      }
    }

    // Use the core function with our generated parsed data
    const plotMapConfig = DEFAULT_PLOT_MAP_CONFIG;

    return await createPlotsFromParsedData(orgId, trialId, experimentId, parsedData, plotMapConfig, onProgress);
  } catch (error) {
    console.error("Error creating plots from grid:", error);
    return {
      success: false,
      createdCount: 0,
      errors: [getFirebaseErrorMessage(error, "create plots")],
    };
  }
};

/**
 * Creates plots from a sequential specification without row/column data.
 * Generates plot IDs as either custom names or sequential numbers.
 */
export const createPlotsFromSequence = async (
  trialId: string,
  experimentId: string,
  count: number,
  customNames?: string[],
  onProgress?: PlotCreationProgressCallback,
  options?: OrgScopedOptions
): Promise<{ success: boolean; createdCount: number; errors?: string[] }> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    // Validate count
    if (count <= 0 || count > MAX_TOTAL_PLOTS) {
      return {
        success: false,
        createdCount: 0,
        errors: [`Invalid plot count: ${count}`],
      };
    }

    // Validate custom names if provided
    if (customNames) {
      if (customNames.length !== count) {
        return {
          success: false,
          createdCount: 0,
          errors: ["Custom names count doesn't match plot count"],
        };
      }

      // Check for empty names
      if (customNames.some((n) => !n.trim())) {
        return {
          success: false,
          createdCount: 0,
          errors: ["All custom names must be non-empty"],
        };
      }

      // Check for duplicates
      const uniqueNames = new Set(customNames.map((n) => n.trim()));
      if (uniqueNames.size !== customNames.length) {
        return {
          success: false,
          createdCount: 0,
          errors: ["Custom names must be unique"],
        };
      }

      // Check for invalid Firestore document ID characters
      const invalidNames = customNames
        .map((name, index) => ({ name: name.trim(), index }))
        .filter(({ name }) => name.includes("/") || name === "." || name === ".." || name.length > 1500);

      if (invalidNames.length > 0) {
        const examples = invalidNames
          .slice(0, 3)
          .map(({ name, index }) => `"${name}" (plot ${index + 1})`)
          .join(", ");
        return {
          success: false,
          createdCount: 0,
          errors: [
            `Invalid plot names detected. Plot names cannot contain "/", be "." or "..", or exceed 1500 characters. ` +
              `Found ${invalidNames.length} invalid name(s): ${examples}${invalidNames.length > 3 ? "..." : ""}`,
          ],
        };
      }
    }

    // Generate parsed data in CSV format for reuse of createPlotsFromParsedData
    const parsedData: Record<string, string>[] = [];

    for (let i = 0; i < count; i++) {
      const plotId = customNames ? customNames[i].trim() : String(i + 1);
      parsedData.push({
        [PLOT_MAP_HEADERS.PLOT_ID]: plotId,
      });
    }

    // Use core function with minimal config
    const plotMapConfig = {
      plotIDName: PLOT_MAP_HEADERS.PLOT_ID,
      headers: [PLOT_MAP_HEADERS.PLOT_ID],
    };

    return await createPlotsFromParsedData(orgId, trialId, experimentId, parsedData, plotMapConfig, onProgress);
  } catch (error) {
    console.error("Error creating plots from sequence:", error);
    return {
      success: false,
      createdCount: 0,
      errors: [getFirebaseErrorMessage(error, "create plots from sequence")],
    };
  }
};

// ============================================
// Plot Metadata Update Operations
// ============================================

/**
 * Result of validating a CSV file for plot metadata updates
 */
export interface PlotMetadataValidationResult {
  isValid: boolean;
  errors: string[];
  matchedPlotIds: string[];
  unmatchedCsvPlotIds: string[];
  duplicatePlotIds: string[];
  rowColumnMismatches: Array<{
    plotId: string;
    csvRow: number;
    csvCol: number;
    existingRow: number;
    existingCol: number;
  }>;
  metadataColumns: string[];
}

/**
 * Result of updating plot metadata from CSV
 */
export interface PlotMetadataUpdateResult {
  success: boolean;
  updatedCount: number;
  errors: string[];
}

/**
 * Validates a CSV file for updating plot metadata without making changes.
 * Returns detailed information about what will change and any validation errors.
 *
 * @param trialId - The trial ID
 * @param experimentId - The experiment ID
 * @param parsedData - Parsed CSV data rows
 * @param existingPlots - Array of existing plots in the experiment
 * @param plotMapConfig - Configuration specifying which columns are plot ID, row, col
 * @returns Validation result with errors or preview of changes
 */
export const validatePlotMetadataUpdate = (
  parsedData: Record<string, string>[],
  existingPlots: Plot[],
  plotMapConfig: {
    plotIDName: string;
    rowIDName?: string;
    colIDName?: string;
  }
): PlotMetadataValidationResult => {
  const errors: string[] = [];
  const matchedPlotIds: string[] = [];
  const unmatchedCsvPlotIds: string[] = [];
  const duplicatePlotIds: string[] = [];
  const rowColumnMismatches: PlotMetadataValidationResult["rowColumnMismatches"] = [];
  const seenPlotIds = new Set<string>();

  // Build a map of existing plots by plotID for O(1) lookup
  const existingPlotsMap = new Map<string, Plot>();
  for (const plot of existingPlots) {
    existingPlotsMap.set(plot.plotID, plot);
  }

  // Validate that plot ID column exists in data
  if (!parsedData || parsedData.length === 0) {
    return {
      isValid: false,
      errors: ["The uploaded file contains no data rows."],
      matchedPlotIds: [],
      unmatchedCsvPlotIds: [],
      duplicatePlotIds: [],
      rowColumnMismatches: [],
      metadataColumns: [],
    };
  }

  if (!plotMapConfig.plotIDName || !(plotMapConfig.plotIDName in parsedData[0])) {
    return {
      isValid: false,
      errors: [
        `Could not find the Plot ID column '${plotMapConfig.plotIDName}' in your file. ` +
          `The experiment requires this column name for plot identification.`,
      ],
      matchedPlotIds: [],
      unmatchedCsvPlotIds: [],
      duplicatePlotIds: [],
      rowColumnMismatches: [],
      metadataColumns: [],
    };
  }

  // Determine metadata columns (all columns except ID columns)
  const allColumns = Object.keys(parsedData[0]);
  const metadataColumns = allColumns.filter(
    (col) => col !== plotMapConfig.plotIDName && col !== plotMapConfig.rowIDName && col !== plotMapConfig.colIDName
  );

  // Validate each row
  for (let i = 0; i < parsedData.length; i++) {
    const row = parsedData[i];
    const plotId = row[plotMapConfig.plotIDName]?.trim();

    if (!plotId) {
      errors.push(`Row ${i + 2}: Missing plot ID in column '${plotMapConfig.plotIDName}'`);
      continue;
    }

    // Check for duplicate plot IDs in CSV
    if (seenPlotIds.has(plotId)) {
      if (!duplicatePlotIds.includes(plotId)) {
        duplicatePlotIds.push(plotId);
      }
      continue;
    }
    seenPlotIds.add(plotId);

    // Check if plot ID exists in experiment
    const existingPlot = existingPlotsMap.get(plotId);
    if (!existingPlot) {
      unmatchedCsvPlotIds.push(plotId);
      continue;
    }

    matchedPlotIds.push(plotId);

    // Helper to validate strict integer strings (reject "1a", "1.5", etc.)
    const isStrictInteger = (value: string | undefined): boolean => {
      if (!value) return false;
      const trimmed = value.trim();
      return /^-?\d+$/.test(trimmed);
    };

    // If row/column columns are specified, verify they match
    if (plotMapConfig.rowIDName && row[plotMapConfig.rowIDName] !== undefined) {
      const csvRowStr = row[plotMapConfig.rowIDName]?.trim();
      if (csvRowStr && !isStrictInteger(csvRowStr)) {
        // Non-integer row value is an error
        errors.push(`Plot ID '${plotId}' has invalid row value '${csvRowStr}'. Row must be an integer.`);
      } else if (csvRowStr) {
        const csvRow = parseInt(csvRowStr, 10);
        if (existingPlot.row !== undefined && csvRow !== existingPlot.row) {
          rowColumnMismatches.push({
            plotId,
            csvRow,
            csvCol:
              plotMapConfig.colIDName && isStrictInteger(row[plotMapConfig.colIDName]?.trim())
                ? parseInt(row[plotMapConfig.colIDName]?.trim(), 10)
                : 0,
            existingRow: existingPlot.row,
            existingCol: existingPlot.column ?? 0,
          });
        }
      }
    }

    if (plotMapConfig.colIDName && row[plotMapConfig.colIDName] !== undefined) {
      const csvColStr = row[plotMapConfig.colIDName]?.trim();
      if (csvColStr && !isStrictInteger(csvColStr)) {
        // Non-integer column value is an error
        errors.push(`Plot ID '${plotId}' has invalid column value '${csvColStr}'. Column must be an integer.`);
      } else if (csvColStr) {
        const csvCol = parseInt(csvColStr, 10);
        if (existingPlot.column !== undefined && csvCol !== existingPlot.column) {
          // Only add if not already added from row mismatch
          const alreadyAdded = rowColumnMismatches.some((m) => m.plotId === plotId);
          if (!alreadyAdded) {
            rowColumnMismatches.push({
              plotId,
              csvRow:
                plotMapConfig.rowIDName && isStrictInteger(row[plotMapConfig.rowIDName]?.trim())
                  ? parseInt(row[plotMapConfig.rowIDName]?.trim(), 10)
                  : 0,
              csvCol,
              existingRow: existingPlot.row ?? 0,
              existingCol: existingPlot.column,
            });
          }
        }
      }
    }
  }

  // Build final error messages
  if (duplicatePlotIds.length > 0) {
    const displayIds =
      duplicatePlotIds.length <= 10
        ? duplicatePlotIds.join(", ")
        : `${duplicatePlotIds.slice(0, 10).join(", ")}... and ${duplicatePlotIds.length - 10} more`;
    errors.push(
      `The following Plot IDs appear multiple times in your file: ${displayIds}. ` +
        `Each plot ID must appear only once.`
    );
  }

  if (unmatchedCsvPlotIds.length > 0) {
    const displayIds =
      unmatchedCsvPlotIds.length <= 10
        ? unmatchedCsvPlotIds.join(", ")
        : `${unmatchedCsvPlotIds.slice(0, 10).join(", ")}... and ${unmatchedCsvPlotIds.length - 10} more`;
    errors.push(
      `The following Plot IDs in your file do not exist in this experiment: ${displayIds}. ` +
        `All plot IDs must match existing plots.`
    );
  }

  if (rowColumnMismatches.length > 0) {
    for (const mismatch of rowColumnMismatches.slice(0, 5)) {
      errors.push(
        `Plot ID '${mismatch.plotId}' has row ${mismatch.csvRow} and column ${mismatch.csvCol} in your file, ` +
          `but the existing plot has row ${mismatch.existingRow} and column ${mismatch.existingCol}. ` +
          `Row and column values cannot be changed.`
      );
    }
    if (rowColumnMismatches.length > 5) {
      errors.push(`...and ${rowColumnMismatches.length - 5} more row/column mismatches.`);
    }
  }

  return {
    isValid: errors.length === 0 && matchedPlotIds.length > 0,
    errors,
    matchedPlotIds,
    unmatchedCsvPlotIds,
    duplicatePlotIds,
    rowColumnMismatches,
    metadataColumns,
  };
};

/**
 * Updates plot metadata from CSV data.
 * This function does NOT check for tasks - metadata updates are safe even when tasks exist.
 * All validation should be done beforehand with validatePlotMetadataUpdate.
 *
 * @param trialId - The trial ID
 * @param experimentId - The experiment ID
 * @param parsedData - Parsed CSV data rows
 * @param plotMapConfig - Configuration specifying which columns are plot ID, row, col
 * @param onProgress - Optional callback for progress updates
 * @returns Result with update count and any errors
 */
export const updatePlotMetadataFromCSV = async (
  trialId: string,
  experimentId: string,
  parsedData: Record<string, string>[],
  plotMapConfig: {
    plotIDName: string;
    rowIDName?: string;
    colIDName?: string;
  },
  onProgress?: (progress: number, message: string) => void
): Promise<PlotMetadataUpdateResult> => {
  const startTime = performance.now();
  console.log(`[MetadataUpdate] Starting for ${parsedData?.length || 0} rows`);

  try {
    if (!parsedData || parsedData.length === 0) {
      return { success: false, updatedCount: 0, errors: ["Parsed data is empty"] };
    }

    const userRef = getUserReference();
    const totalRows = parsedData.length;

    onProgress?.(0, `Preparing metadata updates for ${totalRows.toLocaleString()} plots...`);

    // Build update list from parsed data
    const plotsToUpdate: Array<{
      plotRef: DocumentReference;
      newMetadata: Record<string, string>;
    }> = [];

    for (const row of parsedData) {
      const plotId = row[plotMapConfig.plotIDName]?.trim();
      if (!plotId) continue;

      // Build new metadata (exclude ID columns)
      const newMetadata: Record<string, string> = {};
      for (const [key, value] of Object.entries(row)) {
        if (
          key !== plotMapConfig.plotIDName &&
          key !== plotMapConfig.rowIDName &&
          key !== plotMapConfig.colIDName &&
          value?.trim()
        ) {
          newMetadata[key] = value.trim();
        }
      }

      const plotRef = doc(
        db,
        `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/plots/${plotId}`
      );

      plotsToUpdate.push({ plotRef, newMetadata });
    }

    if (plotsToUpdate.length === 0) {
      return { success: false, updatedCount: 0, errors: ["No valid plots to update"] };
    }

    console.log(`[MetadataUpdate] Preparing ${plotsToUpdate.length} updates`);

    // Create batches for parallel execution
    const batches: BatchWithMetadata[] = [];
    for (let i = 0; i < plotsToUpdate.length; i += BATCH_SIZE) {
      const batchItems = plotsToUpdate.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      for (const { plotRef, newMetadata } of batchItems) {
        batch.update(plotRef, {
          metadata: newMetadata,
          updatedAt: serverTimestamp(),
          updatedBy: userRef,
        });
      }

      batches.push({
        batch,
        plotCount: batchItems.length,
        batchIndex: batches.length,
      });
    }

    console.log(`[MetadataUpdate] Executing ${batches.length} batches (${PARALLEL_BATCHES} parallel)...`);

    // Execute batches in parallel using existing infrastructure
    const result = await executeBatchesInParallel(
      batches,
      PARALLEL_BATCHES,
      plotsToUpdate.length,
      (progress, message) => {
        // Transform the message from "Created X" to "Updated X"
        const updatedMessage = message.replace(/Created/g, "Updated").replace(/plots\.\.\./g, "plots...");
        onProgress?.(progress, updatedMessage);
      }
    );

    const totalTime = performance.now() - startTime;
    console.log(
      `[MetadataUpdate] ${result.success ? "SUCCESS" : "FAILED"}: ${result.createdCount} plots in ${(totalTime / 1000).toFixed(2)}s`
    );

    if (result.success) {
      onProgress?.(1, `Completed! Updated metadata for ${result.createdCount.toLocaleString()} plots.`);
    }

    return {
      success: result.success,
      updatedCount: result.createdCount,
      errors: result.errors,
    };
  } catch (error) {
    console.error("Error updating plot metadata:", error);
    return {
      success: false,
      updatedCount: 0,
      errors: [getFirebaseErrorMessage(error, "update plot metadata")],
    };
  }
};

// EntryCollection operations
export const createEntryCollection = async (
  trialId: string,
  experimentId: string,
  taskId: string,
  entryCollectionData: Partial<EntryCollection>
): Promise<EntryCollection | null> => {
  try {
    const entryCollectionsRef = collection(
      db,
      `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}/entryCollections`
    );
    const userRef = getUserReference();

    // Create a new EntryCollection with empty maps for entries
    const newEntryCollection = {
      plotRef: entryCollectionData.plotRef || doc(db, "plots/mock-plot-1"),
      entryConfigRef: entryCollectionData.entryConfigRef || doc(db, "entryConfigs/mock-entry-config-1"),
      type: entryCollectionData.type || "text",
      isFlagged: entryCollectionData.isFlagged || false,
      isSkipped: entryCollectionData.isSkipped || false,
      textEntries: {}, // Initialize as empty map instead of array
      numberEntries: {},
      categoryEntries: {},
      dateEntries: {},
      photoEntries: {},
      booleanEntries: {},
      percentageEntries: {},
      transcriptionEntries: {},
      leafAreaIndexEntries: {},
      rulerEntries: {},
      imageMeasurementEntries: {},
      standCountMeasurementEntries: {},
      timerEntries: {},
      createdAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
    };

    const docRef = await addDoc(entryCollectionsRef, newEntryCollection);

    return {
      id: docRef.id,
      ...newEntryCollection,
      createdAt: Timestamp.now(),
    } as EntryCollection;
  } catch (error) {
    console.error("Error creating entry collection:", error);
    throw new Error(getFirebaseErrorMessage(error, "create entry collection"));
  }
};

export const updateEntryCollection = async (
  trialId: string,
  experimentId: string,
  taskId: string,
  entryCollectionId: string,
  entryCollectionData: Partial<EntryCollection>
): Promise<boolean> => {
  try {
    const entryCollectionRef = doc(
      db,
      `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}/entryCollections/${entryCollectionId}`
    );
    const userRef = getUserReference();

    const updateData: Partial<EntryCollection> = {
      ...entryCollectionData,
      updatedBy: userRef,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    };

    // Remove id from the update data if present
    delete updateData.id;

    await updateDoc(entryCollectionRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating entry collection:", error);
    throw new Error(getFirebaseErrorMessage(error, "update entry collection"));
  }
};

export const deleteEntryCollection = async (
  trialId: string,
  experimentId: string,
  taskId: string,
  entryCollectionId: string
): Promise<boolean> => {
  try {
    const entryCollectionRef = doc(
      db,
      `organizations/${ORGANIZATION_ID}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}/entryCollections/${entryCollectionId}`
    );
    await deleteDoc(entryCollectionRef);
    return true;
  } catch (error) {
    console.error("Error deleting entry collection:", error);
    throw new Error(getFirebaseErrorMessage(error, "delete entry collection"));
  }
};

// EntryConfig operations
export const createEntryConfig = async (
  entryConfigData: Partial<EntryConfigUnion>
): Promise<EntryConfigUnion | null> => {
  try {
    if (entryConfigData.type === "transcription") {
      console.error("Transcription entry configs are system-managed and cannot be created via the web app.");
      return null;
    }
    const entryConfigsRef = collection(db, `organizations/${ORGANIZATION_ID}/entryConfigs`);
    const userRef = getUserReference();

    const newEntryConfig = {
      name: entryConfigData.name || "New Entry Config",
      description: entryConfigData.description || "",
      isArchived: entryConfigData.isArchived || false,
      type: entryConfigData.type || "text",
      orgId: ORGANIZATION_ID,
      metadata: entryConfigData.metadata || {},
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
      // Add type-specific properties
      ...(entryConfigData.type === "number" && {
        numberConfig: {
          wholeNumbersOnly: entryConfigData.numberConfig?.wholeNumbersOnly,
          min: entryConfigData.numberConfig?.min,
          max: entryConfigData.numberConfig?.max,
        },
      }),
      ...(entryConfigData.type === "category" && {
        categoryConfig: {
          choices: entryConfigData.categoryConfig?.choices || [],
        },
      }),
      ...(entryConfigData.type === "date" && {
        dateConfig: {
          min: entryConfigData.dateConfig?.min,
          max: entryConfigData.dateConfig?.max,
        },
      }),
      ...(entryConfigData.type === "imageMeasurement" && {
        imageMeasurementConfig: {
          imageMeasurementModelOptions: entryConfigData.imageMeasurementConfig?.imageMeasurementModelOptions,
          imageCaptureRequirements: entryConfigData.imageMeasurementConfig?.imageCaptureRequirements,
          objectDetectionDefaultOptions: entryConfigData.imageMeasurementConfig?.objectDetectionDefaultOptions,
          ...(entryConfigData.imageMeasurementConfig?.grading && {
            grading: entryConfigData.imageMeasurementConfig.grading,
          }),
        },
      }),
      ...(entryConfigData.type === "standCountMeasurement" && {
        standCountMeasurementConfig: {
          ...(entryConfigData.standCountMeasurementConfig?.imgMeasureConfig && {
            imgMeasureConfig: entryConfigData.standCountMeasurementConfig.imgMeasureConfig,
          }),
          ...(entryConfigData.standCountMeasurementConfig?.boxLengthMeters != null && {
            boxLengthMeters: entryConfigData.standCountMeasurementConfig.boxLengthMeters,
          }),
          ...(entryConfigData.standCountMeasurementConfig?.boxWidthMeters != null && {
            boxWidthMeters: entryConfigData.standCountMeasurementConfig.boxWidthMeters,
          }),
        },
      }),
      ...(entryConfigData.type === "leafAreaIndex" && {
        leafAreaIndexConfig: {
          useFixedBoxSize: entryConfigData.leafAreaIndexConfig?.useFixedBoxSize,
          defaultSensitivity: entryConfigData.leafAreaIndexConfig?.defaultSensitivity,
          boxLengthMeters: entryConfigData.leafAreaIndexConfig?.boxLengthMeters,
          boxWidthMeters: entryConfigData.leafAreaIndexConfig?.boxWidthMeters,
          imageCaptureRequirements: entryConfigData.leafAreaIndexConfig?.imageCaptureRequirements,
        },
      }),
      ...(entryConfigData.type === "ruler" && {
        rulerConfig: {
          minLengthMeters: entryConfigData.rulerConfig?.minLengthMeters,
          maxLengthMeters: entryConfigData.rulerConfig?.maxLengthMeters,
        },
      }),
      ...(entryConfigData.type === "photo" && {
        photoConfig: {
          imageCaptureRequirements: entryConfigData.photoConfig?.imageCaptureRequirements,
        },
      }),
      ...(entryConfigData.type === "rowPlantedStandCount" && {
        rowPlantedStandCountConfig: entryConfigData.rowPlantedStandCountConfig,
      }),
      ...(entryConfigData.type === "timer" && {
        timerConfig: {
          startStopOnBarcodeScan: entryConfigData.timerConfig?.startStopOnBarcodeScan,
        },
      }),
    };

    const docRef = await addDoc(entryConfigsRef, newEntryConfig);

    return {
      id: docRef.id,
      ...newEntryConfig,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    } as EntryConfigUnion;
  } catch (error) {
    console.error("Error creating entry config:", error);
    throw new Error(getFirebaseErrorMessage(error, "create trait"));
  }
};

export const updateEntryConfig = async (
  entryConfigId: string,
  entryConfigData: Partial<EntryConfigUnion>,
  options?: OrgScopedOptions
): Promise<boolean> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    const entryConfigRef = doc(db, `organizations/${orgId}/entryConfigs/${entryConfigId}`);
    const userRef = getUserReference();

    const updateData: Partial<EntryConfigUnion> = {
      ...entryConfigData,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    delete updateData.id;

    await timeFirestoreOp(`updateEntryConfig(${entryConfigId})`, () =>
      updateDoc(entryConfigRef, updateData)
    );
    return true;
  } catch (error) {
    console.error("Error updating entry config:", error);
    throw new Error(getFirebaseErrorMessage(error, "update trait"));
  }
};

export const deleteEntryConfig = async (entryConfigId: string, options?: OrgScopedOptions): Promise<boolean> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    const entryConfigRef = doc(db, `organizations/${orgId}/entryConfigs/${entryConfigId}`);
    await timeFirestoreOp(`deleteEntryConfig(${entryConfigId})`, () => deleteDoc(entryConfigRef));
    return true;
  } catch (error) {
    console.error("Error deleting entry config:", error);
    throw new Error(getFirebaseErrorMessage(error, "delete trait"));
  }
};

/**
 * Archive a trait (set isArchived: true). Used traits are archived instead of
 * hard-deleted so their collected data stays visible and exportable. Reuses
 * updateEntryConfig so updatedAt/updatedBy are stamped consistently.
 */
export const archiveEntryConfig = async (entryConfigId: string, options?: OrgScopedOptions): Promise<boolean> => {
  return updateEntryConfig(entryConfigId, { isArchived: true }, options);
};

/** Restore an archived trait (set isArchived: false). */
export const restoreEntryConfig = async (entryConfigId: string): Promise<boolean> => {
  return updateEntryConfig(entryConfigId, { isArchived: false });
};

/** A single place a trait is referenced: a task, with its trial context + ids for linking. */
export interface TraitUsage {
  trialId: string;
  experimentId: string;
  taskId: string;
  trialName: string;
  taskName: string;
}

/**
 * Check whether a trait (EntryConfig) is referenced by any task in the current
 * organization, returning the per-task usages (trial + task + ids) plus the
 * distinct trial names.
 *
 * Walks the org hierarchy (trials → experiments → tasks) with normal nested
 * reads that org members are permitted to make. A cross-org collectionGroup
 * query over `tasks` is NOT usable here: the collection-group rule restricts
 * those reads to super admins (firestore.rules), and tasks carry no orgId.
 */
export const checkEntryConfigUsage = async (
  entryConfigId: string,
  options?: OrgScopedOptions
): Promise<{ isUsed: boolean; trialNames?: string[]; usages?: TraitUsage[] }> => {
  const orgId = options?.orgId ?? requireOrganizationId();
  try {
    return await timeFirestoreOp(`checkEntryConfigUsage(${entryConfigId})`, async () => {
      const taskReferencesConfig = (taskData: Record<string, unknown>): boolean => {
        const configs = taskData?.dataCollectionConfigs as
          | Array<{ entryConfigRef?: DocumentReference | string }>
          | undefined;
        if (!Array.isArray(configs)) return false;
        return configs.some((c) => {
          const ref = c?.entryConfigRef;
          const refId = typeof ref === "string" ? ref : ref?.id;
          return refId === entryConfigId;
        });
      };

      const trialsSnapshot = await getDocs(
        collection(db, `organizations/${orgId}/trials`)
      );

      const perTrialUsages = await Promise.all(
        trialsSnapshot.docs.map(async (trialDoc) => {
          const trialName = (trialDoc.data().name as string) || "Untitled Trial";
          const usages: TraitUsage[] = [];
          const experimentsSnapshot = await getDocs(
            collection(
              db,
              `organizations/${orgId}/trials/${trialDoc.id}/experiments`
            )
          );
          for (const experimentDoc of experimentsSnapshot.docs) {
            const tasksSnapshot = await getDocs(
              collection(
                db,
                `organizations/${orgId}/trials/${trialDoc.id}/experiments/${experimentDoc.id}/tasks`
              )
            );
            for (const taskDoc of tasksSnapshot.docs) {
              if (taskReferencesConfig(taskDoc.data())) {
                usages.push({
                  trialId: trialDoc.id,
                  experimentId: experimentDoc.id,
                  taskId: taskDoc.id,
                  trialName,
                  taskName: (taskDoc.data().name as string) || "Untitled Task",
                });
              }
            }
          }
          return usages;
        })
      );

      const usages = perTrialUsages.flat();
      const trialNames = Array.from(new Set(usages.map((u) => u.trialName)));
      return { isUsed: usages.length > 0, trialNames, usages };
    });
  } catch (error) {
    console.error("Error checking entry config usage:", error);
    return { isUsed: false, usages: [] };
  }
};

// File upload operations
export const uploadCSVFile = async (
  file: File,
  trialId: string,
  experimentId: string,
  options?: UploadCSVFileOptions
): Promise<UploadResult> => {
  return storageUploadCSVFile(file, trialId, experimentId, options);
};

// Crop operations
export const createCrop = async (cropData: Partial<Crop>): Promise<Crop | null> => {
  try {
    const cropsRef = collection(db, `organizations/${ORGANIZATION_ID}/crops`);
    const userRef = getUserReference();

    const newCrop = {
      name: cropData.name || "New Crop",
      description: cropData.description || "",
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
    };

    const docRef = await addDoc(cropsRef, newCrop);

    return {
      id: docRef.id,
      ...newCrop,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    } as Crop;
  } catch (error) {
    console.error("Error creating crop:", error);
    throw new Error(getFirebaseErrorMessage(error, "create crop"));
  }
};

export const updateCrop = async (cropId: string, cropData: Partial<Crop>): Promise<boolean> => {
  try {
    const cropRef = doc(db, `organizations/${ORGANIZATION_ID}/crops/${cropId}`);
    const userRef = getUserReference();

    const updateData: Partial<Crop> = {
      ...cropData,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    delete updateData.id;

    await updateDoc(cropRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating crop:", error);
    throw new Error(getFirebaseErrorMessage(error, "update crop"));
  }
};

export const deleteCrop = async (cropId: string): Promise<boolean> => {
  try {
    const cropRef = doc(db, `organizations/${ORGANIZATION_ID}/crops/${cropId}`);
    await deleteDoc(cropRef);
    return true;
  } catch (error) {
    console.error("Error deleting crop:", error);
    throw new Error(getFirebaseErrorMessage(error, "delete crop"));
  }
};

// Location operations
export const createLocation = async (locationData: Partial<Location>): Promise<Location | null> => {
  try {
    const locationsRef = collection(db, `organizations/${ORGANIZATION_ID}/locations`);
    const userRef = getUserReference();

    const newLocation = {
      name: locationData.name || "New Location",
      description: locationData.description || "",
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      createdBy: userRef,
      updatedBy: userRef,
    };

    const docRef = await addDoc(locationsRef, newLocation);

    return {
      id: docRef.id,
      ...newLocation,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    } as Location;
  } catch (error) {
    console.error("Error creating location:", error);
    throw new Error(getFirebaseErrorMessage(error, "create location"));
  }
};

export const updateLocation = async (locationId: string, locationData: Partial<Location>): Promise<boolean> => {
  try {
    const locationRef = doc(db, `organizations/${ORGANIZATION_ID}/locations/${locationId}`);
    const userRef = getUserReference();

    const updateData: Partial<Location> = {
      ...locationData,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      updatedBy: userRef,
    };

    delete updateData.id;

    await updateDoc(locationRef, updateData);
    return true;
  } catch (error) {
    console.error("Error updating location:", error);
    throw new Error(getFirebaseErrorMessage(error, "update location"));
  }
};

export const deleteLocation = async (locationId: string): Promise<boolean> => {
  try {
    const locationRef = doc(db, `organizations/${ORGANIZATION_ID}/locations/${locationId}`);
    await deleteDoc(locationRef);
    return true;
  } catch (error) {
    console.error("Error deleting location:", error);
    throw new Error(getFirebaseErrorMessage(error, "delete location"));
  }
};
